import React, { useEffect } from 'react';
import { Route, Navigate, Routes } from 'react-router-dom';

import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import {
  ADVICES_URL,
  ARTICLES_URL,
  CHAT_URL,
  EXPERT_ADMIN_URL,
  INSTRUCTIONS_URL,
  INTERVENTION_PLANS_URL,
  MOVIES_URL,
  NOTICES_URL,
  PROFILE_URL,
  REPORTS_URL,
  RESET_PASSWORD_URL,
  SCHOOLS_URL,
  SET_PASSWORD_URL,
  EXPERT_URL,
  USERS_URL,
} from 'App/constants';

import { notificationTypes } from 'utils/constants';
import { userRoles } from 'utils/userRoles';
import { api, getApiUrl, ResponseError, Violence } from 'utils/api';

import PrivateRoute from 'containers/PrivateRoute';
import RetrievePassword from 'pages/RetrievePassword';
import ResetPassword from 'pages/ResetPassword';
import Login from 'pages/Login';
import Users from 'pages/Users';
import Profile from 'pages/Profile';
import Schools from 'pages/Schools';
import Chat from 'pages/Chat';
import Notices from 'pages/Notices';
import Advices from 'pages/Advices';
import Instructions from 'pages/Instructions';
import Reports from 'pages/Reports';
import Movies from 'pages/Movies';
import Articles from 'pages/Articles';
import Notifications from 'components/Notifications';
import ResqlExpertAdmin from 'pages/ResqlExpertAdmin';
import InterventionPlans from 'pages/InterventionPlans';
import ResqlExpert from 'pages/ResqlExpert';
import Layout from 'components/Layout';
import Navigation from 'components/Navigation';

const url = getApiUrl('/violence-types');

const HomeApp: React.FC = () => {
  const [{ profile, notifications }, dispatch] = useApp();

  const loadViolenceTypes = async () => {
    try {
      const response: Violence[] = await api(url, {
        method: 'GET',
      });
      if (response) {
        dispatch({ type: ActionTypes.GET_VIOLENCE_TYPES, payload: response });
      }
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  useEffect(() => {
    if (profile) loadViolenceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <Layout>
      {!!profile && <Navigation />}
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path={`/${SET_PASSWORD_URL}`} element={<RetrievePassword />} />
        <Route path={`/${RESET_PASSWORD_URL}`} element={<ResetPassword />} />
        <Route
          path={`/${REPORTS_URL}`}
          element={
            <PrivateRoute
              Component={Reports}
              requiredRole={[userRoles.admin, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${CHAT_URL}`}
          element={
            <PrivateRoute
              Component={Chat}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${EXPERT_ADMIN_URL}`}
          element={<PrivateRoute Component={ResqlExpertAdmin} requiredRole={[userRoles.admin]} />}
        />
        <Route
          path={`/${EXPERT_URL}`}
          element={
            <PrivateRoute
              Component={ResqlExpert}
              requireResqlExpert={true}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${INTERVENTION_PLANS_URL}`}
          element={
            <PrivateRoute
              Component={InterventionPlans}
              requireResqlExpert={true}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${USERS_URL}`}
          element={<PrivateRoute Component={Users} requiredRole={[userRoles.admin]} />}
        />
        <Route
          path={`/${SCHOOLS_URL}`}
          element={<PrivateRoute Component={Schools} requiredRole={[userRoles.admin]} />}
        />
        <Route path={`/${PROFILE_URL}`} element={<PrivateRoute Component={Profile} />} />
        <Route
          path={`/${NOTICES_URL}`}
          element={<PrivateRoute Component={Notices} requiredRole={[userRoles.admin]} />}
        />
        <Route
          path={`/${ADVICES_URL}`}
          element={
            <PrivateRoute
              Component={Advices}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${INSTRUCTIONS_URL}`}
          element={
            <PrivateRoute
              Component={Instructions}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${MOVIES_URL}`}
          element={
            <PrivateRoute
              Component={Movies}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route
          path={`/${ARTICLES_URL}`}
          element={
            <PrivateRoute
              Component={Articles}
              requiredRole={[userRoles.intervener, userRoles.director]}
            />
          }
        />
        <Route path="*" element={<Navigate to="/app" />} />
      </Routes>
      {!!notifications.length && <Notifications notifications={notifications} />}
    </Layout>
  );
};

export default HomeApp;
